// FLAGS: none
// Session-type by language toggle grid; saves which pairs are closed for one expert.
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import Button from "@openup/shared/components/Button/Button";
import {
  ModelsTag,
  RequestExpertsSessionTypeLanguagePairRequest,
} from "@src/api/v1/data-contracts";
import { useUpdateExpertLanguageAvailability } from "@src/queries/language-availability";
import { WithSnackbarProps } from "../SnackBarComponent";

interface LanguageAvailabilityFormProps extends WithSnackbarProps {
  allExpertSessionTypes: ModelsTag[];
  expertLanguages: ModelsTag[];
  initialClosedPairs: RequestExpertsSessionTypeLanguagePairRequest[];
  mutation: ReturnType<typeof useUpdateExpertLanguageAvailability>;
  onClose: () => void;
}

const LanguageAvailabilityForm = ({
  allExpertSessionTypes,
  expertLanguages,
  initialClosedPairs,
  mutation,
  onClose,
  snackbarShowMessage,
}: LanguageAvailabilityFormProps) => {
  const { t } = useTranslation();

  const visibleLanguages = useMemo(
    () => expertLanguages.filter((l) => !!l.name),
    [expertLanguages],
  );

  const [closedPairs, setClosedPairs] =
    useState<RequestExpertsSessionTypeLanguagePairRequest[]>(
      initialClosedPairs,
    );
  const [activeOverride, setActiveOverride] = useState<string | null>(null);
  const activeSessionType =
    activeOverride ?? allExpertSessionTypes[0]?.name ?? null;

  const isClosed = (sessionType: string, language: string) =>
    closedPairs.some(
      (p) => p.sessionType === sessionType && p.language === language,
    );

  const handleToggle = (language: string) => {
    if (!activeSessionType) return;
    setClosedPairs((prev) =>
      isClosed(activeSessionType, language)
        ? prev.filter(
            (p) =>
              !(p.sessionType === activeSessionType && p.language === language),
          )
        : [...prev, { sessionType: activeSessionType, language }],
    );
  };

  const handleSave = () => {
    mutation.mutate(closedPairs, {
      onSuccess: () => {
        onClose();
      },
      onError: () => {
        snackbarShowMessage(t("LanguageAvailability.SaveError"), "error");
      },
    });
  };

  const totalLanguages = visibleLanguages.length;
  const closedForActiveCount = activeSessionType
    ? closedPairs.filter((p) => p.sessionType === activeSessionType).length
    : 0;
  const selectedCount = totalLanguages - closedForActiveCount;

  return (
    <>
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <aside className="w-56 flex-shrink-0 border-r border-slate-200 [border-right-style:solid] overflow-y-auto">
          <h3 className="text-sm font-medium text-blue-500 px-4">
            {t("LanguageAvailability.SessionType")}
          </h3>
          <ul className="list-none p-0 m-0">
            {allExpertSessionTypes.map((sessionType) => {
              const isActive = activeSessionType === sessionType.name;
              return (
                <li key={sessionType.name}>
                  <button
                    type="button"
                    onClick={() => setActiveOverride(sessionType.name ?? null)}
                    className={clsx(
                      "w-full text-left px-4 py-2 border-0 cursor-pointer",
                      isActive
                        ? "bg-blue-500/10 text-blue-500 font-semibold"
                        : "text-indigo-800 bg-transparent",
                    )}
                  >
                    {t(`SessionType.${sessionType.name}`)}
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>
        <section className="flex-1 overflow-y-auto">
          <div className="flex items-center justify-between px-4">
            <h3 className="text-sm font-medium text-blue-500">
              {t("LanguageAvailability.LanguagesFor", {
                sessionType: activeSessionType
                  ? t(`SessionType.${activeSessionType}`)
                  : "",
              })}
            </h3>
            <span className="text-xs text-slate-500">
              {t("LanguageAvailability.SelectedCount", {
                selected: selectedCount,
                total: totalLanguages,
              })}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 px-4 pb-4">
            {visibleLanguages.map((language) => {
              const name = language.name ?? "";
              const isEnabled =
                !activeSessionType || !isClosed(activeSessionType, name);
              return (
                <label
                  key={name}
                  className="flex items-center gap-2 cursor-pointer text-sm select-none"
                >
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={() => handleToggle(name)}
                    className="size-4 accent-indigo-800 cursor-pointer"
                  />
                  {language.description ?? name}
                </label>
              );
            })}
          </div>
        </section>
      </div>
      <div className="flex justify-end gap-3 px-4 py-3 border-t border-slate-200 [border-top-style:solid]">
        <Button
          variant="tertiary"
          onClick={onClose}
          disabled={mutation.isPending}
          className="!outline-0"
        >
          {t("Common.Cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          loading={mutation.isPending}
          disabled={mutation.isPending}
        >
          {t("Common.Save")}
        </Button>
      </div>
    </>
  );
};

export default LanguageAvailabilityForm;
